import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@core-ui/Components/ui/tabs';
import { Card, CardDescription, CardHeader, CardTitle } from '@core-ui/Components/ui/card';
import { usePaginationData } from '@hooks/usePaginationData';
import { PaginationControls } from '@ui/shared/paginationControls';
import useContactsViewModel from '@features/contacts/hooks/useContactsViewModel';
import ContactListSidebar from '@features/contacts/components/ContactListSidebar';
import ContactsTable from '@features/contacts/components/ContactsTable';
import ContactsHeader from '@features/contacts/components/ContactsHeader';
import CreateListDialog from '@features/contacts/components/CreateListDialog';
import DeleteListDialog from '@features/contacts/components/DeleteListDialog';
import EditContactDialog from '@features/contacts/components/EditContactDialog';
import DeleteContactDialog from '@features/contacts/components/DeleteContactDialog';
import type {
  ContactInfoDto,
  ContactListDto,
  ContactsTab,
  UpdateContactDto
} from '@features/contacts/types/contacts.types';
import { tabToSource } from '@features/contacts/types/contacts.types';
import { useFeatureFlagsStore } from '@store/featureFlags';
import { useOutreachViewModel } from '@features/contacts/hooks/useOutreachViewModel.ts';
import { type FeaturesDto, type IntegrationAccount } from '@types';
import { Integrations } from '@features/Settings/enums/IntegrationsEnums.ts';
import { useHubspotViewModel } from '@features/contacts/hooks/useHubspotViewModel.ts';
import { useSalesforceViewModel } from '@features/contacts/hooks/useSalesforceViewModel.ts';
import { SalesforceDebugDialog } from '@features/contacts/components/SalesforceDebugDialog.tsx';

export default function ContactsDashboard (): React.JSX.Element {
  const { t } = useTranslation();
  const featureFlags = useFeatureFlagsStore((s) => s.flags);

  const {
    organizationId,
    activeTab,
    setActiveTab,
    contactLists,
    contactListsLoadingState,
    selectedContactListId,
    setSelectedContactListId,
    selectedContactList,
    contacts,
    totalContacts,
    totalWarnings,
    refreshContactLists,
    refreshContacts,
    createContactList,
    deleteContactList,
    updateContact,
    deleteContact,
    uploadCsvToContactList,
    downloadTemplateCsv,
    exportContactsToCsv,
  } = useContactsViewModel();
  const { retrieveOutreachAccounts } = useOutreachViewModel();
  const { retrieveHubspotAccounts } = useHubspotViewModel();
  const { retrieveSalesforceAccounts, retrieveSalesforceContacts, debugData } = useSalesforceViewModel();

  const [searchQuery, setSearchQuery] = useState('');

  
  useEffect(() => {
    setSearchQuery('');
    setKey((prevState)=> prevState + 1);
  }, [activeTab]);
  
  const [isCreateListDialogOpen, setIsCreateListDialogOpen] = useState(false);
  const [newContactListTitle, setNewContactListTitle] = useState('');
  const [newContactListFile, setNewContactListFile] = useState<File | null>(null);
  const [isCreatingContactList, setIsCreatingContactList] = useState(false);

  const [isDeleteListDialogOpen, setIsDeleteListDialogOpen] = useState(false);
  const [contactListToDelete, setContactListToDelete] = useState<ContactListDto | null>(null);
  const [isDeletingContactList, setIsDeletingContactList] = useState(false);

  const [isEditContactDialogOpen, setIsEditContactDialogOpen] = useState(false);
  const [contactToEdit, setContactToEdit] = useState<ContactInfoDto | null>(null);
  const [isSavingContact, setIsSavingContact] = useState(false);

  const [isDeleteContactDialogOpen, setIsDeleteContactDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<ContactInfoDto | null>(null);

  const [key, setKey] = useState(0);

  const outreachAccountsCount = useRef(0);
  const hubspotAccountsCount = useRef(0);
  const salesforceAccountsCount = useRef(0);

  const [outreachConfigured, setOutreachConfigured] = useState( featureFlags.outreach && outreachAccountsCount.current !== 0);
  const [hubspotConfigured, setHubspotConfigured] = useState( featureFlags.hubspot && hubspotAccountsCount.current !== 0);
  const [salesforceConfigured, setSalesforceConfigured] = useState(featureFlags.salesforce && salesforceAccountsCount.current !== 0);

  const activeSource = tabToSource(activeTab);

  const [isDebugDisplayed, displayDebugDialog] = useState(false);

  const handleOpenDebug = async (): Promise<void> => {
    if (selectedContactListId == null) {
      return;
    }

    try {
      await retrieveSalesforceContacts('', selectedContactListId);
    } finally {
      displayDebugDialog(true);
    }
  };

  const filteredContacts = useMemo(() => {
    const normalizedQuery = searchQuery.toLowerCase().trim();
    const contactsList = Array.isArray(contacts) ? contacts : [];

    if (normalizedQuery === '') {
      return contactsList;
    }

    return contactsList.filter((contact) => {
      const searchableFields = [
        contact.name,
        contact.phone,
        contact.accountName,
        contact.jobTitle,
        contact.email,
        contact.notes,
      ];

      return searchableFields
        .filter(Boolean)
        .some((fieldValue) => String(fieldValue).toLowerCase().includes(normalizedQuery));
    });
  }, [contacts, searchQuery]);

  const displayedContactsCount = contactListsLoadingState !== 'SUCCESS' ?  0 : totalContacts;
  const displayedWarningsCount = selectedContactList?.warningsCount ?? totalWarnings;

  const {
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    currentData: paginatedContacts,
    totalItems,
  } = usePaginationData<ContactInfoDto>({
    mode: 'client',
    data: filteredContacts,
    initialRowsPerPage: 10,
  });

  const handleOpenCreateListDialog = (): void => {
    setNewContactListTitle('');
    setNewContactListFile(null);
    setIsCreateListDialogOpen(true);
  };

  const handleCreateContactListSubmit = async (): Promise<void> => {
    const title = newContactListTitle.trim();
    if (title === '' || newContactListFile === null) {
      return;
    }

    setIsCreatingContactList(true);
    try {
      if (organizationId === '') {
        return;
      }

      const newContactListId = await createContactList({
        organizationId,
        title,
        type: 'CSV',
        nonce: 'CSV',
      });

      if (newContactListId !== null && newContactListFile !== null) {
        await uploadCsvToContactList({
          organizationId,
          contactListId: newContactListId,
          file: newContactListFile,
        });

        await refreshContactLists();
        await refreshContacts( { force: true });
      }

      setIsCreateListDialogOpen(false);
    } finally {
      setIsCreatingContactList(false);
    }
  };

  const handleRequestDeleteContactList = (contactList: ContactListDto): void => {
    setContactListToDelete(contactList);
    setIsDeleteListDialogOpen(true);
  };

  const handleConfirmDeleteContactList = async (): Promise<void> => {
    if (contactListToDelete?.id === undefined) {
      return;
    }

    setIsDeletingContactList(true);
    try {
      await deleteContactList(contactListToDelete.id);
      setIsDeleteListDialogOpen(false);
      setContactListToDelete(null);
    } finally {
      setIsDeletingContactList(false);
    }
  };

  const handleRequestEditContact = (contact: ContactInfoDto): void => {
    setContactToEdit(contact);
    setIsEditContactDialogOpen(true);
  };

  const handleSaveContact = async (contactData: UpdateContactDto): Promise<void> => {
    setIsSavingContact(true);
    try {
      await updateContact(contactData);
      setIsEditContactDialogOpen(false);
      setContactToEdit(null);
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleRequestDeleteContact = (contact: ContactInfoDto): void => {
    setContactToDelete(contact);
    setIsDeleteContactDialogOpen(true);
  };

  const handleConfirmDeleteContact = async (): Promise<void> => {
    if (contactToDelete?.id === undefined) {
      return;
    }

    setIsSavingContact(true);
    try {
      await deleteContact(contactToDelete.id);
      setIsDeleteContactDialogOpen(false);
      setContactToDelete(null);
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleExportContacts = (): void => {
    exportContactsToCsv({
      contacts: filteredContacts,
      filename: selectedContactList?.title,
    });
  };

  const handleResyncOutreach = async (): Promise<void> => {
    try {
      await refreshContactLists();
      setSelectedContactListId(selectedContactListId);
    } finally {
      await refreshContacts( { force: true }, true);
    }
  };

  const defineIntegrationIsConfigured = async (
    integrationName: string,
    integrations: FeaturesDto,
    callback: () => Promise<string | IntegrationAccount[]>,
    setterHook: React.Dispatch<React.SetStateAction<boolean>>
  ): Promise<void> => {
    const isIntegrationOn = integrations[integrationName as keyof FeaturesDto];

    if (!isIntegrationOn) {
      setterHook(false);
      return;
    }

    try {
      const accounts = await callback();
      if (Array.isArray(accounts)) {
        setterHook(accounts.length > 0);
      } else {
        setterHook(false);
      }
    } catch {
      setterHook(false);
    }
  };

  useEffect(() => {
    void defineIntegrationIsConfigured(Integrations.Outreach.toLowerCase(), featureFlags, retrieveOutreachAccounts, setOutreachConfigured);
    void defineIntegrationIsConfigured(Integrations.Hubspot.toLowerCase(), featureFlags, retrieveHubspotAccounts, setHubspotConfigured);
    void defineIntegrationIsConfigured(Integrations.Salesforce.toLowerCase(), featureFlags, retrieveSalesforceAccounts, setSalesforceConfigured);
  }, []);

  return (
    <div>
      <Card className="p-6 rounded-2xl shadow-lg border border-border bg-card text-foreground">
        <CardHeader className="px-0 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-2xl font-bold">{t('contacts.title')}</CardTitle>
              <CardDescription className="mt-1">
                {t('contacts.description')}
              </CardDescription>
            </div>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={(tabValue) => {
              setActiveTab(tabValue as ContactsTab);
            }}
            className="w-full"
          >
            <TabsList data-qa-id="contacts-tab-list"  className="bg-transparent flex w-full md:w-[340px] p-1 rounded-xl gap-2">
              <TabsTrigger value="csv" className="tab-trigger p-0 rounded-xl">CSV</TabsTrigger>
              {featureFlags.outreach && (
                <TabsTrigger value="outreach" className="tab-trigger p-0 rounded-xl">{t('contacts.tabs.outreach')}</TabsTrigger>
              )}
              {featureFlags.hubspot && (
                <TabsTrigger  value="hubspot" className="tab-trigger p-0 rounded-xl">{t('contacts.tabs.hubspot')}</TabsTrigger>
              )}
              {featureFlags.salesforce && (
                <TabsTrigger value="salesforce" className="tab-trigger py-0 px-1 rounded-xl">{t('contacts.tabs.salesforce')}</TabsTrigger>
              )}
            </TabsList>
            <TabsContent value={activeTab} className="mt-2">
              <div key={key} className="grid w-full grid-cols-1 gap-4 md:grid-cols-[340px_1fr]">
                {/* LEFT: Contact Lists Sidebar */}
                <ContactListSidebar
                  contactLists={contactLists ?? []}
                  selectedContactListId={selectedContactListId}
                  onSelectContactList={(contactListId) => {
                    setSelectedContactListId(contactListId);
                  }}
                  onDeleteContactList={(contactListId) => {
                    const contactList = contactLists?.find(
                      (list) => list.id === contactListId,
                    );
                    if (contactList !== undefined) {
                      handleRequestDeleteContactList(contactList);
                    }
                  }}
                  onCreateContactList={handleOpenCreateListDialog}
                  isLoading={contactListsLoadingState === 'LOADING' || contactListsLoadingState === 'IDLE'}
                  activeTab={activeTab}
                  outreachConnected={outreachConfigured}
                  hubspotConnected={hubspotConfigured}
                  salesforceConnected={salesforceConfigured}
                  contactsCount={displayedContactsCount}
                />

                {/* RIGHT: Contacts Table */}
                <div className="space-y-4">
                  <ContactsHeader
                    contactListTitle={selectedContactList?.title ?? null}
                    contactsCount={displayedContactsCount}
                    warningsCount={displayedWarningsCount}
                    searchQuery={searchQuery}
                    onSearchQueryChange={setSearchQuery}
                    onClearSearch={() => {
                      setSearchQuery('');
                    } }
                    activeSource={activeSource}
                    onExportContacts={handleExportContacts}
                    isExportDisabled={filteredContacts.length === 0}
                    showFilters={activeSource !== 'csv'} key={0}
                    refreshSequences={handleResyncOutreach}
                    debugData={debugData ?? null}
                    onDebugClick={handleOpenDebug}
                    isListSelected={selectedContactList?.id !== undefined}
                    outreachConfigured={outreachConfigured}
                    salesforceConfigured={salesforceConfigured}
                  />

                  <SalesforceDebugDialog
                    isOpen={isDebugDisplayed}
                    onOpenChange={displayDebugDialog}
                    data={debugData}
                  />

                  <ContactsTable
                    isLoading={contactListsLoadingState === 'LOADING' || contactListsLoadingState === 'IDLE'}
                    contacts={paginatedContacts}
                    skeletonRowsCount={rowsPerPage}
                    onEditContact={handleRequestEditContact}
                    onDeleteContact={(contactId) => {
                      const contact = filteredContacts.find(
                        (contactItem) => contactItem.id === contactId,
                      );
                      if (contact !== undefined) {
                        handleRequestDeleteContact(contact);
                      }
                    }}
                    activeTab={activeTab}
                  />

                  {totalItems > 0 && (
                    <PaginationControls
                      totalItems={totalItems}
                      currentPage={currentPage}
                      onPageChange={(p) => {
                        setCurrentPage(Math.max(1, Math.min(p, Math.ceil(totalItems / rowsPerPage))));
                      }}
                      rowsPerPage={rowsPerPage}
                      setRowsPerPage={(n) => {
                        setRowsPerPage(n);
                        setCurrentPage(1);
                      }}
                      rowsPerPageOptions={[5, 10, 15, 20]}
                    />
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardHeader>
      </Card>

      {/* Dialogs */}
      <CreateListDialog
        isOpen={isCreateListDialogOpen}
        onOpenChange={setIsCreateListDialogOpen}
        contactListTitle={newContactListTitle}
        onContactListTitleChange={setNewContactListTitle}
        csvFile={newContactListFile}
        onCsvFileChange={setNewContactListFile}
        isLoading={isCreatingContactList}
        onSubmit={() => {
          void handleCreateContactListSubmit();
        }}
        onDownloadTemplate={() => {
          downloadTemplateCsv();
        }}
      />

      <DeleteListDialog
        isOpen={isDeleteListDialogOpen}
        onOpenChange={setIsDeleteListDialogOpen}
        contactList={contactListToDelete}
        isLoading={isDeletingContactList}
        onConfirmDelete={() => {
          void handleConfirmDeleteContactList();
        }}
      />

      <EditContactDialog
        isOpen={isEditContactDialogOpen}
        onOpenChange={setIsEditContactDialogOpen}
        contact={contactToEdit}
        isLoading={isSavingContact}
        onSaveContact={(contactData) => {
          void handleSaveContact(contactData);
        }}
      />

      <DeleteContactDialog
        isOpen={isDeleteContactDialogOpen}
        onOpenChange={setIsDeleteContactDialogOpen}
        contact={contactToDelete}
        isLoading={isSavingContact}
        onConfirmDelete={() => {
          void handleConfirmDeleteContact();
        }}
      />
    </div>
  );
}
